import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { s } from '@/theme/tokens';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Box, Text } from '@/ui/primitives';
import type { DashboardRange } from '@/features/dashboard/hooks';
import type { ReactElement } from 'react';

type SearchTagsDateHeaderFiltersProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchAriaLabel: string;
  selectedTags: string[];
  onTagsChange: (nextTags: string[]) => void;
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  selectedStatuses?: string[];
  onStatusesChange?: (nextStatuses: string[]) => void;
  statusOptions?: Array<{ value: string; label: string }>;
  range: DashboardRange;
  onRangeChange: (nextRange: DashboardRange) => void;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  hasCustomDates: boolean;
  isLatestMode: boolean;
  onSelectedStartDayChange: (nextDay: string | null) => void;
  onSelectedEndDayChange: (nextDay: string | null) => void;
  includeLatest?: boolean;
};

export const SearchTagsDateHeaderFilters = ({
  searchValue,
  onSearchChange,
  searchAriaLabel,
  selectedTags,
  onTagsChange,
  tagOptions,
  tagOptionsLoading,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  range,
  onRangeChange,
  pickerStartDay,
  pickerEndDay,
  hasCustomDates,
  isLatestMode,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  includeLatest = true,
}: SearchTagsDateHeaderFiltersProps): ReactElement => {
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: s('md'),
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: s('md'),
          minWidth: 0,
          flex: { xs: '1 1 100%', md: '1 1 280px' },
        }}
      >
        <Text role="label" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
          Search
        </Text>
        <Box
          sx={{
            width: { xs: fixedFilterWidth, md: '100%' },
            minWidth: { xs: fixedFilterWidth, md: 0 },
            maxWidth: { xs: fixedFilterWidth, md: 'none' },
            flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 auto' },
          }}
        >
          <SearchFilterField
            value={searchValue}
            inputAriaLabel={searchAriaLabel}
            compact
            fullWidth
            controlHeight={32}
            onChange={(event) => {
              onSearchChange(event.target.value);
            }}
            sx={{ width: '100%' }}
          />
        </Box>
      </Box>
      {selectedStatuses && onStatusesChange && statusOptions ? (
        <Box
          sx={{
            width: { xs: fixedFilterWidth, md: '100%' },
            minWidth: { xs: fixedFilterWidth, md: 220 },
            maxWidth: { xs: fixedFilterWidth, md: 'none' },
            flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 220px' },
          }}
        >
          <TagsFilterField
            label="Status"
            inputAriaLabel="Filter by status"
            value={selectedStatuses}
            options={statusOptions}
            onChange={onStatusesChange}
            emptyLabel="All Statuses"
            fullWidth
          />
        </Box>
      ) : null}
      <Box
        sx={{
          width: { xs: fixedFilterWidth, md: '100%' },
          minWidth: { xs: fixedFilterWidth, md: 220 },
          maxWidth: { xs: fixedFilterWidth, md: 'none' },
          flex: { xs: `0 0 ${fixedFilterWidth}`, md: '1 1 220px' },
        }}
      >
        <TagsFilterField
          inputAriaLabel="Filter by tags"
          value={selectedTags}
          options={tagOptions}
          onChange={onTagsChange}
          emptyLabel={tagOptionsLoading ? 'Loading Tags...' : 'All Tags'}
          fullWidth
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <DashboardDateRangeControls
          value={range}
          onChange={onRangeChange}
          includeLatest={includeLatest}
          fullWidthOnMobile
          displayStartDay={pickerStartDay}
          displayEndDay={pickerEndDay}
          isCustomRange={hasCustomDates && !isLatestMode}
          onSelectedStartDayChange={onSelectedStartDayChange}
          onSelectedEndDayChange={onSelectedEndDayChange}
          showQuickFilters={false}
          showDatePickers
        />
      </Box>
      <Box sx={{ minWidth: 0, marginLeft: { md: 'auto' } }}>
        <DashboardDateRangeControls
          value={range}
          onChange={onRangeChange}
          includeLatest={includeLatest}
          fullWidthOnMobile={false}
          showDatePickers={false}
          showQuickFilters
          buttonSx={{
            minHeight: 32,
            height: 32,
            paddingY: s('xxs'),
            paddingX: s('md'),
            lineHeight: 1,
          }}
        />
      </Box>
    </Box>
  );
};

export type { SearchTagsDateHeaderFiltersProps };
